import { t } from "@lingui/macro";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { alpha, styled, useTheme } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useUser } from "@/client/services/user";

// ─── Types ────────────────────────────────────────────────────────────────────
type AssessmentItem = { status: string; score: number | null; totalScore: number; category: string };
type InterviewItem  = { status: string; score: number | null; totalScore: number; category: string };
type EventItem      = { audience_status: string };
type PlacementJob   = { id: string; title: string; salary: number };
type PlacementItem  = { title: string; jobs: PlacementJob[]; start_date: string; end_date: string; company?: { name: string }; type: string };

interface Stats {
  assessments:     { total: number; completed: number; open: number; upcoming: number; missed: number; avgScore: number };
  interviews:      { total: number; completed: number; open: number; upcoming: number; missed: number; avgScore: number };
  coding:          { total: number; completed: number; open: number; upcoming: number; missed: number; avgScore: number };
  selfAssessments: { total: number; completed: number; open: number; upcoming: number; missed: number };
  selfInterviews:  { total: number; completed: number; open: number; upcoming: number; missed: number };
  selfCoding:      { total: number; completed: number; open: number; upcoming: number; missed: number };
  events:          { total: number; accepted: number; invited: number; declined: number };
  placements:      { total: number; jobs: number };
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  success: "#16a34a",
  warning: "#d97706",
  info:    "#0284c7",
  danger:  "#dc2626",
  primary: "#2563eb",
  violet:  "#7c3aed",
  teal:    "#0f766e",
  pink:    "#be185d",
  orange:  "#c2410c",
  sky:     "#0369a1",
};

// ─── Styled shells ────────────────────────────────────────────────────────────
const Shell = styled(Box)(({ theme }) => ({
  padding: "14px 16px",
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(160deg,#080e1a 0%,#0d1424 100%)"
      : "linear-gradient(160deg,#f8fafc 0%,#f0f4f8 100%)",
  minHeight: "100%",
}));

const Card = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  padding: "14px 16px",
  border: `1px solid ${theme.palette.mode === "dark" ? alpha("#7dd3fc", 0.15) : "#dbeafe"}`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 2px 12px rgba(0,0,0,0.35)"
      : "0 1px 6px rgba(15,23,42,0.06)",
  background:
    theme.palette.mode === "dark"
      ? alpha("#111827", 0.9)
      : alpha("#ffffff", 0.95),
}));

// ─── Animated counter ─────────────────────────────────────────────────────────
function useAnimatedCount(target: number, duration = 800) {
  const [count, setCount] = useState(0);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    const start = performance.now();
    const animate = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      setCount(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);
  return count;
}

const Num = ({ v, color, px = 22 }: { v: number; color?: string; px?: number }) => {
  const n = useAnimatedCount(v);
  return (
    <Typography
      sx={{
        fontSize: px,
        fontWeight: 700,
        lineHeight: 1,
        color: color ?? "text.primary",
        fontVariantNumeric: "tabular-nums lining-nums",
        letterSpacing: "-0.025em",
      }}
    >
      {n}
    </Typography>
  );
};

// ─── KPI card ─────────────────────────────────────────────────────────────────
const KpiCard = ({
  title, value, sub, color, icon,
}: { title: string; value: number; sub: string; color: string; icon: React.ReactNode }) => (
  <Card elevation={0} sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
    {/* subtle tinted gradient wash */}
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
      <Box>
        <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.secondary", fontWeight: 700, mb: 0.75 }}>
          {title}
        </Typography>
        <Num v={value} color={color} px={26} />
        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5, lineHeight: 1.3 }}>{sub}</Typography>
      </Box>
      <Box sx={{ width: 34, height: 34, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: alpha(color, 0.1), color, flexShrink: 0, "& svg": { fontSize: 17 } }}>
        {icon}
      </Box>
    </Stack>
  </Card>
);

// ─── Score ring ───────────────────────────────────────────────────────────────
const ScoreRing = ({
  title, value, color, icon,
}: { title: string; value: number; color: string; icon: React.ReactNode }) => (
  <Card elevation={0}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{title}</Typography>
      <Box sx={{ color, "& svg": { fontSize: 16 } }}>{icon}</Box>
    </Stack>
    <Box sx={{ position: "relative", height: 130 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={[{ v: value }, { v: Math.max(100 - value, 0) }]}
            dataKey="v" innerRadius={40} outerRadius={54}
            startAngle={90} endAngle={-270}
            stroke="none" isAnimationActive animationDuration={700}
          >
            <Cell fill={color} />
            <Cell fill={alpha(color, 0.12)} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
        <Box sx={{ textAlign: "center" }}>
          <Num v={value} color={color} px={20} />
          <Typography sx={{ fontSize: 9, color: "text.secondary", mt: 0.3 }}>avg %</Typography>
        </Box>
      </Box>
    </Box>
  </Card>
);

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const TTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: "background.paper", border: "1px solid #dbeafe", borderRadius: 1.5, px: 1.25, py: 0.75, boxShadow: 3 }}>
      {label && <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>{label}</Typography>}
      {payload.map((e: any, i: number) => (
        <Typography key={i} sx={{ fontSize: 11, color: e.color ?? "text.secondary", fontVariantNumeric: "tabular-nums" }}>
          {e.name}: {e.value}
        </Typography>
      ))}
    </Box>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const DashboardReportPage = () => {
  const theme = useTheme();
  const { user } = useUser();
  const userId        = user?.email;       // assessment & interview endpoints
  const selfStudentId = user?.id;          // self-assessment / self-interview / self-coding (cuid)
  const studentId     = user?.studentId;   // events, placements, internship-jobs (uuid)

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    assessments:     { total: 0, completed: 0, open: 0, upcoming: 0, missed: 0, avgScore: 0 },
    interviews:      { total: 0, completed: 0, open: 0, upcoming: 0, missed: 0, avgScore: 0 },
    coding:          { total: 0, completed: 0, open: 0, upcoming: 0, missed: 0, avgScore: 0 },
    selfAssessments: { total: 0, completed: 0, open: 0, upcoming: 0, missed: 0 },
    selfInterviews:  { total: 0, completed: 0, open: 0, upcoming: 0, missed: 0 },
    selfCoding:      { total: 0, completed: 0, open: 0, upcoming: 0, missed: 0 },
    events:          { total: 0, accepted: 0, invited: 0, declined: 0 },
    placements:      { total: 0, jobs: 0 },
  });

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [assessRes, interviewRes, codingRes, selfAssessRes, selfInterviewRes, selfCodingRes, eventsRes, placementRes] =
          await Promise.allSettled([
            // assessments, interviews & coding use email
            axios.get(`/api/integration/external-service/assessment`,     { params: { email: userId } }),
            axios.get(`/api/integration/external-service/interview`,      { params: { email: userId } }),
            axios.get(`/api/integration/external-service/coding`,         { params: { email: userId } }),
            // self-* endpoints use user?.id (cuid) — matches SelfAssessments / SelfInterviews / SelfCoding pages
            axios.get(`/api/integration/external-service/self-assessment`,{ params: { studentId: selfStudentId, page: 1, limit: 10000 } }),
            axios.get(`/api/integration/external-service/self-interview`, { params: { studentId: selfStudentId, page: 1, limit: 10000 } }),
            axios.get(`/api/integration/external-service/self-coding`,    { params: { studentId: selfStudentId, page: 1, limit: 10000 } }),
            // events, placements use user?.studentId (uuid)
            axios.get(`/api/placement/events/${studentId}`),
            axios.get(`/api/placement/${studentId}`),
          ]);

        // Robust extractor — handles all known response shapes:
        //   { data: [...] }
        //   { result: [...] }
        //   { result: { data: [...] } }
        //   { jobs: [...] }
        const getList = (res: PromiseSettledResult<any>): any[] => {
          if (res.status === "rejected") return [];
          const d = res.value?.data;
          if (!d) return [];
          // shape: { result: { data: [...] } }   ← self-assessment returns this
          if (Array.isArray(d?.result?.data))  return d.result.data;
          // shape: { result: [...] }
          if (Array.isArray(d?.result))        return d.result;
          // shape: { data: [...] }
          if (Array.isArray(d?.data))          return d.data;
          // shape: { jobs: [...] }
          if (Array.isArray(d?.jobs))          return d.jobs;
          // shape: top-level array
          if (Array.isArray(d))                return d;
          return [];
        };

        const assessList      = getList(assessRes)       as AssessmentItem[];
        const interviewList   = getList(interviewRes)    as InterviewItem[];
        const codingList      = getList(codingRes)       as AssessmentItem[];
        const selfAssessList  = getList(selfAssessRes)   as AssessmentItem[];
        const selfInterviewList = getList(selfInterviewRes) as InterviewItem[];
        const selfCodingList  = getList(selfCodingRes)   as AssessmentItem[];
        const eventsList      = getList(eventsRes)       as EventItem[];
        const placementList   = getList(placementRes)    as PlacementItem[];

        // Normal modules: upcoming / open / completed / missed
        const calcStatus = (list: { status: string }[]) => ({
          total:     list.length,
          completed: list.filter(i => i.status?.toLowerCase() === "completed").length,
          open:      list.filter(i => i.status?.toLowerCase() === "open").length,
          upcoming:  list.filter(i => i.status?.toLowerCase() === "upcoming").length,
          missed:    list.filter(i => i.status?.toLowerCase() === "missed").length,
        });

        // Self-* modules: draft / published / completed
        // published → open (ready to take), draft → upcoming (not yet available), no missed
        const calcSelfStatus = (list: { status: string }[]) => ({
          total:     list.length,
          completed: list.filter(i => i.status?.toLowerCase() === "completed").length,
          open:      list.filter(i => i.status?.toLowerCase() === "published").length,
          upcoming:  list.filter(i => i.status?.toLowerCase() === "draft").length,
          missed:    0,
        });

        const calcAvg = (list: { score: number | null; totalScore: number; status: string }[]) => {
          const s = list.filter(i => i.score !== null && i.totalScore > 0 && i.status?.toLowerCase() === "completed");
          if (!s.length) return 0;
          return Math.round(s.reduce((a, i) => a + (i.score! / i.totalScore) * 100, 0) / s.length);
        };

        setStats({
          assessments:     { ...calcStatus(assessList),      avgScore: calcAvg(assessList) },
          interviews:      { ...calcStatus(interviewList),   avgScore: calcAvg(interviewList) },
          coding:          { ...calcStatus(codingList),      avgScore: calcAvg(codingList) },
          selfAssessments: calcSelfStatus(selfAssessList),
          selfInterviews:  calcSelfStatus(selfInterviewList),
          selfCoding:      calcSelfStatus(selfCodingList),
          events: {
            total:    eventsList.length,
            accepted: eventsList.filter(e => e.audience_status === "accepted").length,
            invited:  eventsList.filter(e => e.audience_status === "invited").length,
            declined: eventsList.filter(e => e.audience_status === "declined").length,
          },
          placements: { total: placementList.length, jobs: placementList.reduce((s, p) => s + (p.jobs?.length ?? 0), 0) },
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (userId || selfStudentId || studentId) fetchAll();
  }, [userId, selfStudentId, studentId]);

  // ── Aggregates ──────────────────────────────────────────────────────────────
  const totalCompleted = stats.assessments.completed + stats.interviews.completed + stats.coding.completed + stats.selfAssessments.completed + stats.selfInterviews.completed + stats.selfCoding.completed + stats.events.accepted;
  const totalOpen      = stats.assessments.open + stats.interviews.open + stats.coding.open + stats.selfAssessments.open + stats.selfInterviews.open + stats.selfCoding.open + stats.events.invited;
  const totalUpcoming  = stats.assessments.upcoming + stats.interviews.upcoming + stats.coding.upcoming + stats.selfAssessments.upcoming + stats.selfInterviews.upcoming + stats.selfCoding.upcoming;
  const totalMissed    = stats.assessments.missed + stats.interviews.missed + stats.coding.missed + stats.selfAssessments.missed + stats.selfInterviews.missed + stats.selfCoding.missed;
  const totalActivities = useMemo(() => stats.assessments.total + stats.interviews.total + stats.coding.total + stats.selfAssessments.total + stats.selfInterviews.total + stats.selfCoding.total + stats.events.total + stats.placements.total, [stats]);

  const pct = (n: number, d: number) => (d === 0 ? 0 : Math.round((n / d) * 100));

  // ── Data arrays ─────────────────────────────────────────────────────────────
  const kpis = [
    { title: t`Completed`,    value: totalCompleted, sub: t`Across all activities`,   color: T.success, icon: <CheckCircleOutlineIcon /> },
    { title: t`Open / Active`,value: totalOpen,      sub: t`Needs your action`,        color: T.warning, icon: <PendingOutlinedIcon /> },
    { title: t`Upcoming`,     value: totalUpcoming,  sub: t`Scheduled ahead`,          color: T.info,    icon: <ScheduleOutlinedIcon /> },
    { title: t`Missed`,       value: totalMissed,    sub: t`Expired without attempt`,  color: T.danger,  icon: <WarningAmberOutlinedIcon /> },
  ];

  const moduleRows = [
  { label: t`Assessments`,      missed: stats.assessments.missed,      total: stats.assessments.total,      completed: stats.assessments.completed,     open: stats.assessments.open,     upcoming: stats.assessments.upcoming,     color: T.primary, icon: <AssignmentOutlinedIcon sx={{ fontSize: 15 }} /> },
  { label: t`Interviews`,       missed: stats.interviews.missed,       total: stats.interviews.total,       completed: stats.interviews.completed,      open: stats.interviews.open,      upcoming: stats.interviews.upcoming,      color: T.violet,  icon: <MicNoneOutlinedIcon sx={{ fontSize: 15 }} /> },
  { label: t`Coding`,           missed: stats.coding.missed,           total: stats.coding.total,           completed: stats.coding.completed,          open: stats.coding.open,          upcoming: stats.coding.upcoming,          color: T.sky,     icon: <CodeOutlinedIcon sx={{ fontSize: 15 }} /> },
  { label: t`Self Assessments`, missed: 0,                             total: stats.selfAssessments.total,  completed: stats.selfAssessments.completed, open: stats.selfAssessments.open, upcoming: stats.selfAssessments.upcoming, color: T.teal,    icon: <QueryStatsOutlinedIcon sx={{ fontSize: 15 }} /> },
  { label: t`Self Interviews`,  missed: 0,                             total: stats.selfInterviews.total,   completed: stats.selfInterviews.completed,  open: stats.selfInterviews.open,  upcoming: stats.selfInterviews.upcoming,  color: T.pink,    icon: <VideoCallOutlinedIcon sx={{ fontSize: 15 }} /> },
  { label: t`Self Coding`,      missed: 0,                             total: stats.selfCoding.total,       completed: stats.selfCoding.completed,      open: stats.selfCoding.open,      upcoming: stats.selfCoding.upcoming,      color: T.orange,  icon: <CodeOutlinedIcon sx={{ fontSize: 15 }} /> },
  { label: t`Events`,           missed: stats.events.declined,         total: stats.events.total,           completed: stats.events.accepted,           open: stats.events.invited,       upcoming: 0,                              color: "#c2410c", icon: <EventOutlinedIcon sx={{ fontSize: 15 }} /> },
];

  const trendData = moduleRows.map(r => ({ name: r.label, [t`Done`]: r.completed, [t`Open`]: r.open, [t`Upcoming`]: r.upcoming }));

  const distData = [
  { name: t`Assessments`,      value: stats.assessments.total,      color: alpha(T.primary, 0.85) },
  { name: t`Interviews`,       value: stats.interviews.total,       color: alpha(T.violet, 0.85) },
  { name: t`Coding`,           value: stats.coding.total,           color: alpha(T.sky, 0.85) },
  { name: t`Self Assessments`, value: stats.selfAssessments.total,  color: alpha(T.teal, 0.85) },
  { name: t`Self Interviews`,  value: stats.selfInterviews.total,   color: alpha(T.pink, 0.85) },
  { name: t`Self Coding`,      value: stats.selfCoding.total,       color: alpha(T.orange, 0.85) },
  { name: t`Events`,           value: stats.events.total,           color: alpha(T.warning, 0.85) },
  { name: t`Placements`,       value: stats.placements.total,       color: alpha(T.success, 0.85) },
  // { name: t`Jobs`,             value: stats.internshipJobs.total,   color: alpha(T.info, 0.85) },
].filter(d => d.value > 0);



  const isDark = theme.palette.mode === "dark";
  const gridLine = alpha("#94a3b8", isDark ? 0.15 : 0.2);
  const tickColor = theme.palette.text.secondary as string;

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress size={28} thickness={3} />
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>{t`Loading dashboard…`}</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Shell>
      <Stack spacing={2}>

        {/* ── Page header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", letterSpacing: "-0.01em" }}>
              {t`Student Dashboard`}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
              {t`Activity overview across all modules`}
            </Typography>
          </Box>
        </Stack>

        {/* ── Row 1 — KPI cards ── */}
        <Grid container spacing={1.5}>
          {kpis.map(k => (
            <Grid item xs={12} sm={6} lg={3} key={k.title as string}>
              <KpiCard title={k.title as string} value={k.value} sub={k.sub as string} color={k.color} icon={k.icon} />
            </Grid>
          ))}
        </Grid>

        {/* ── Row 2 — Area chart + donut ── */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={8}>
            <Card elevation={0} sx={{ height: 360 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{t`Module activity breakdown`}</Typography>
                  <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{t`Completed · open · upcoming per module`}</Typography>
                </Box>
              </Stack>
              <ResponsiveContainer width="100%" height="82%">
                <AreaChart data={trendData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                  <defs>
                    {[
                      { id: "gDone", color: T.success },
                      { id: "gOpen", color: T.warning },
                      { id: "gUp",   color: T.info    },
                    ].map(g => (
                      <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={g.color} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={g.color} stopOpacity={0.02} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridLine} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: tickColor }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: tickColor }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<TTip />} />
                  <Area type="monotone" dataKey={t`Done`}     stroke={alpha(T.success, 0.75)} fill="url(#gDone)" strokeWidth={2} />
                  <Area type="monotone" dataKey={t`Open`}     stroke={alpha(T.warning, 0.75)} fill="url(#gOpen)" strokeWidth={2} />
                  <Area type="monotone" dataKey={t`Upcoming`} stroke={alpha(T.info, 0.75)}    fill="url(#gUp)"   strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
              {/* inline legend */}
              <Stack direction="row" spacing={2} sx={{ mt: 0.75, pl: 0.5 }}>
                {[{ label: t`Done`, color: T.success }, { label: t`Open`, color: T.warning }, { label: t`Upcoming`, color: T.info }].map(l => (
                  <Stack key={l.label as string} direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: l.color }} />
                    <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{l.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card elevation={0} sx={{ height: 360, display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{t`Activity mix`}</Typography>
              <Typography sx={{ fontSize: 10, color: "text.secondary", mb: 1 }}>{t`Share by module`}</Typography>

              <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 1 }}>
                {/* Donut — left */}
                <Box sx={{ flex: "0 0 240px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={distData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={45}
                        outerRadius={80}
                        paddingAngle={2}
                        stroke="none"
                        isAnimationActive
                        animationDuration={700}
                      >
                        {distData.map(e => <Cell key={e.name as string} fill={e.color} />)}
                      </Pie>
                      <Tooltip content={<TTip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                {/* Legend list — right */}
                <Stack
                  spacing={0.6}
                  justifyContent="center"
                  sx={{ flex: 1, minWidth: 0, pr: 0.5 }}
                >
                  {distData.map(e => (
                    <Stack
                      key={e.name as string}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={0.75}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.6} sx={{ minWidth: 0 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: e.color, flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {e.name}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t`Total activities`}</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {totalActivities}
                </Typography>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 3 — Module table + right column ── */}
        <Grid container spacing={1.5}>

          {/* Module performance table */}
          <Grid item xs={12} lg={8}>
  <Card elevation={0}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{t`Module performance`}</Typography>
    <Typography sx={{ fontSize: 10, color: "text.secondary", mb: 1.5 }}>
      {t`Dense statistical view across all modules`}
    </Typography>

    {/* Header row — added Missed + Draft columns */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          "minmax(130px,1.6fr) 46px 46px 46px 46px minmax(110px,1fr)",
        gap: 1,
        px: 1,
        pb: 0.75,
        borderBottom: "1px solid #dbeafe",
      }}
    >
      {[t`Module`, t`Done`, t`Open`, t`Draft`, t`Missed`, t`Completion`].map((h, i) => (
        <Typography
          key={i}
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: "text.secondary",
            textAlign: i > 0 && i < 5 ? "center" : "left",
          }}
        >
          {h}
        </Typography>
      ))}
    </Box>

    <Stack spacing={0.75} sx={{ mt: 0.75 }}>
      {moduleRows.map(row => {
        const completion = pct(row.completed, row.total);
        return (
          <Box
            key={row.label as string}
            sx={{
              display: "grid",
              gridTemplateColumns:
                "minmax(130px,1.6fr) 46px 46px 46px 46px minmax(110px,1fr)",
              gap: 1,
              alignItems: "center",
              px: 1,
              py: 0.9,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "#dbeafe",
              bgcolor: isDark ? alpha("#fff", 0.02) : alpha("#f8fafc", 0.7),
              transition: "border-color .15s",
              "&:hover": {
                borderColor: alpha(row.color, 0.5),
                bgcolor: alpha(row.color, 0.02),
              },
            }}
          >
            {/* Label */}
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(row.color, 0.1),
                  color: row.color,
                  flexShrink: 0,
                }}
              >
                {row.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.label}
                </Typography>
                {/* Sub-hint: shows "published + open" for self-* modules */}
                <Typography sx={{ fontSize: 9, color: "text.secondary" }}>
                  {row.open > 0 ? `${row.open} ${t`open`}` : ""}
                  {row.open > 0 && row.upcoming > 0 ? " · " : ""}
                  {row.upcoming > 0 ? `${row.upcoming} ${t`draft`}` : ""}
                </Typography>
              </Box>
            </Stack>

            {/* Done */}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                textAlign: "center",
                color: T.success,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.completed}
            </Typography>

            {/* Open (includes published for self-* modules) */}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textAlign: "center",
                color: T.warning,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.open}
            </Typography>

            {/* Draft / Upcoming */}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textAlign: "center",
                color: T.info,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.upcoming}
            </Typography>

            {/* Missed */}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textAlign: "center",
                color: row.missed > 0 ? T.danger : "text.disabled",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.missed}
            </Typography>

            {/* Completion bar */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
              >
                <Typography sx={{ fontSize: 9, color: "text.secondary" }}>
                  {completion}%
                </Typography>
                <Typography sx={{ fontSize: 9, color: row.color, fontWeight: 700 }}>
                  {row.completed}/{row.total}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completion}
                sx={{
                  height: 5,
                  borderRadius: 99,
                  bgcolor: alpha(row.color, 0.12),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 99,
                    background: `linear-gradient(90deg, ${row.color} 0%, ${alpha(row.color, 0.7)} 100%)`,
                  },
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  </Card>
</Grid>

          {/* Right sidebar */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={1.5}>

              {/* Score rings (conditional) */}
              {stats.assessments.avgScore > 0 && (
                <ScoreRing title={t`Assessment average`} value={stats.assessments.avgScore} color={T.primary} icon={<AssignmentOutlinedIcon />} />
              )}
              {stats.interviews.avgScore > 0 && (
                <ScoreRing title={t`Interview average`} value={stats.interviews.avgScore} color={T.violet} icon={<VideoCallOutlinedIcon />} />
              )}
              {stats.coding.avgScore > 0 && (
                <ScoreRing title={t`Coding average`} value={stats.coding.avgScore} color={T.sky} icon={<CodeOutlinedIcon />} />
              )}

              {/* Snapshot */}
              <Card elevation={0}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1.25 }}>{t`Snapshot`}</Typography>
                <Stack spacing={0.9}>
                  {[
                    { icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 14, color: T.success }} />, label: t`Placements`,       value: stats.placements.total     },
                    { icon: <WorkHistoryOutlinedIcon   sx={{ fontSize: 14, color: T.info    }} />, label: t`Placement roles`,  value: stats.placements.jobs      },
                    { icon: <EventOutlinedIcon         sx={{ fontSize: 14, color: T.warning }} />, label: t`Accepted events`,  value: stats.events.accepted      },
                  ].map((row, i) => (
                    <Stack key={i} direction="row" justifyContent="space-between" alignItems="center"
                      sx={{ py: 0.6, borderBottom: i < 2 ? "1px solid" : "none", borderColor: "#e0f0ff" }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {row.icon}
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{row.label}</Typography>
                      </Stack>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{row.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Card>

            </Stack>
          </Grid>
        </Grid>

      </Stack>
    </Shell>
  );
};

export default DashboardReportPage;